<script setup>
import { ref, computed, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useRouteQuery } from '@vueuse/router';

import { useToast } from 'primevue/usetoast';
import InputText from 'primevue/inputtext';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Tag from 'primevue/tag';

import usePanelsService from '@/services/panels';
import { displayTypes } from '@/config/interactiveTypes';

import useDataTableDefaults from '@/composables/useDataTableDefaults';
import useInlineRowEdits from '@/composables/useInlineRowEdits';
import useScopedListSearch from '@/composables/useScopedListSearch';

import RowMenu from '@/components/ListViewComponents/RowMenu.vue';
import DataTableHeader from '@/components/ListViewComponents/DataTableHeader.vue';
import ListSearchEmptyState from '@/components/ListViewComponents/ListSearchEmptyState.vue';
import PreviewThumbnail from '@/components/ListViewComponents/PreviewThumbnail.vue';
import TagPicker from '@/components/TagPicker.vue';

const toast = useToast();
const ROWS_PER_PAGE = 10;
const tableDefaults = useDataTableDefaults(ROWS_PER_PAGE);
const panelsService = usePanelsService();
const $router = useRouter();

const {
  baseDataTableProps,
  checkboxColumnProps,
  editRowColumnProps,
  tagsProps,
  makeFormattedDate,
  handleSort,
} = tableDefaults;

const {
  editingRows,
  onRowClick,
  onRowEditCancel,
  onRowEditInit,
  onEditRowComplete,
  showInlineEditSuccess,
} = useInlineRowEdits();

const selectedRows = ref();
const rowsPerPage = ref(ROWS_PER_PAGE);
const currentPage = useRouteQuery('page', '1', { transform: Number });
const firstPage = computed(() => (currentPage.value - 1) * rowsPerPage.value);
const searchStr = useScopedListSearch('smartDisplays');
const sortBy = useRouteQuery('sortBy', '');
const sortDir = useRouteQuery('sortDir', '');

watch(searchStr, () => {
  currentPage.value = 1;
});

const selectedTags = useRouteQuery('tags', [], {
  transform: (val) => (Array.isArray(val) ? val : [val]),
});

const displaysQuery = computed(() => ({
  page: currentPage.value,
  types: displayTypes,
  tags: selectedTags.value,
  search: searchStr.value,
  sortBy: sortBy.value,
  sortDir: sortDir.value,
}));

const {
  data: displaysData,
  isFetching: isFetchingDisplays,
  execute: fetchDisplays,
} = panelsService.getPanels(displaysQuery, { immediate: true, refetch: true });

const displays = computed(() => displaysData.value?.panels ?? []);
const totalDisplays = computed(() => displaysData.value?.total ?? 0);

const tagQuery = computed(() => ({
  types: displayTypes,
}));

const {
  data: tagsData,
  isFetching: isFetchingTags,
  execute: getTags,
} = panelsService.getTags(tagQuery, { refetch: true });

const tags = computed(() => tagsData.value?.tags ?? []);

const displaysColumns = ref([
  { props: checkboxColumnProps },
  { props: editRowColumnProps },
  {
    field: 'preview',
    customBody: true,
  },
  {
    field: 'title',
    header: 'Title',
    sortable: true,
  },
  {
    field: 'type',
    header: 'Type',
    sortable: true,
    customBody: true,
  },
  {
    field: 'tags',
    header: 'Tags',
    customBody: true,
  },
  {
    field: 'modified_time',
    header: 'Modified Date',
    customBody: true,
    sortable: true,
  },
  {
    field: 'modified_by',
    header: 'Modified By',
    sortable: true,
  },
  {
    contextualMenu: true,
  },
]);

// may extract this later
const editRowComplete = async (newData) => {
  await panelsService.editPanel(newData._id, newData, {
    async afterFetch() {
      showInlineEditSuccess('displays', toast);
      fetchDisplays();
      getTags();
    },
  });
};

const makeRowHref = (e) => `${window.location.origin}/panel/${e.data._id}`;

const onClickPanelRow = (e) => {
  const panelId = e.data._id;

  $router.push({
    name: 'panel-editor',
    params: { panelId },
  });
};

// const toDuplicate = ref(null);
const disableActions = ref(false);

// row menu callback functions
const duplicatePanel = async (item, onSuccess) => {
  // toDuplicate = ref(item);
  await panelsService.duplicatePanel(item._id, {
    beforeFetch() {
      // if we need to disable the button while fetching
      disableActions.value = true;
    },
    afterFetch({ data }) {
      disableActions.value = false;
      onSuccess(data.title);
      fetchDisplays();
    },
  });
};

const deleteDisplay = async (item, onSuccess) => {
  await panelsService.deletePanel(item._id, {
    beforeFetch() {
      // if we need to disable the button while fetching
      disableActions.value = true;
    },
    afterFetch() {
      disableActions.value = false;
      onSuccess(item.title);
      fetchDisplays();
    },
  });
};

const contextualMenus = ref([]);

const isRunningBulkAction = ref(false);
const bulkDelete = async (onSuccess) => {
  isRunningBulkAction.value = true;

  const allDeletePromises = selectedRows.value.map((item) => panelsService.deletePanel(item._id, {
    afterFetch() {
      onSuccess(item.title);
    },
  }));

  await Promise.allSettled(allDeletePromises);

  selectedRows.value = [];
  isRunningBulkAction.value = false;
  fetchDisplays();
};

const sortTable = (event) => {
  currentPage.value = 1;
  const { field, order } = handleSort(event);
  sortBy.value = field;
  sortDir.value = order;
};

</script>

<template>
  <DataTable
    v-bind="baseDataTableProps"
    v-model:selection="selectedRows"
    v-model:editing-rows="editingRows"
    :value="displays"
    :rows="rowsPerPage"
    :first="firstPage"
    :total-records="totalDisplays"
    :loading="isFetchingDisplays || isRunningBulkAction"
    editMode="row"
    dataKey="_id"
    rowHover
    removable-sort
    @row-edit-save="onEditRowComplete($event, editRowComplete)"
    @row-edit-cancel="onRowEditCancel"
    @row-edit-init="onRowEditInit"
    @page="currentPage = $event.page + 1"
    @row-click="onRowClick($event, onClickPanelRow)"
    @sort="sortTable"
    pt:root:class="listTable__interactive-displays"
  >
    <template #header>
      <DataTableHeader
        :search="false"
        v-model:selectedTags="selectedTags"
        :tags="tags"
        :isFetchingTags="isFetchingTags"
        :selectedRows="selectedRows"
        :bulkDelete="bulkDelete"
        componentType="interactiveDisplay"
      />
    </template>
    <template #empty>
      <ListSearchEmptyState
        v-if="searchStr"
        :query="searchStr"
        scope-label="Graphics (Classic)"
      />
      <p v-else>No displays found.</p>
    </template>
    <Column
      v-for="(column, idx) in displaysColumns"
      v-bind="column.props"
      :key="`column-${idx}`"
      :field="column.field"
      :header="column.header"
      :sortable="column.sortable"
    >
      <template
        v-if="column.customBody"
        #body="{ data, field }"
      >
        <template v-if="field === 'preview'">
          <!-- TODO: add "retry" event listener when backend is fixed -->
          <PreviewThumbnail
            :url="data.preview_url"
            alt="Display Preview"
          />
        </template>
        <template v-else-if="field === 'type'">
          <div>
            {{ data.type }}
            <Tag
              v-if="['heatmap', 'youtube', 'vimeo'].includes(data.type)"
              value="Edit Only"
              rounded
              class="ml-2 surface-400"
            />
          </div>
        </template>
        <template v-else-if="field === 'tags'">
          <TagPicker
            v-model="data.tags"
            :tag-list-props="tagsProps"
            view-only
          />
        </template>

        <template v-else-if="field === 'modified_time'">
          {{ makeFormattedDate(data.modified_time) }}
        </template>
      </template>
      <template
        v-else-if="column.contextualMenu"
        #body="{ data }"
      >
        <RowMenu
          ref="contextualMenus"
          :isDisabled="disableActions"
          :key="`row-menu-${data._id}`"
          :data="data"
          :actions="{
            makeACopy: {
              duplicate: duplicatePanel,
            },
            openInNewTab: {
              url: makeRowHref({ data }),
            },
            copyLink: {
              linkToCopy: makeRowHref({ data }),
            },
            delete: {
              deleteItem: deleteDisplay,
            },
          }" />
      </template>
      <template
        v-if="column.field === 'title' || column.field === 'tags'"
        #editor="{ data, field }"
      >
        <template v-if="field === 'title'">
          <InputText
            :pt:root="{
              'data-entity': `editTitleInline-${data._id}`,
              'data-test': 'edit-title-inline',
            }"
            v-model="data[field]"
            autofocus
          />
        </template>
        <template v-if="field === 'tags'">
          <TagPicker
            v-model="data[field]"
            :tag-list-props="tagsProps"
            @create-tag="getTags"
          />
        </template>
      </template>
    </Column>
  </DataTable>
</template>
<style lang="scss">
  .listTable__interactive-displays {
    max-height: 80vh;
    .listTable__row td{
      &:last-child {
        text-align: right;
      }
    }
  }
</style>
